import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Terminal, LogOut, User, CreditCard, Send, 
  Activity, TrendingUp, Clock, CheckCircle, 
  XCircle, AlertTriangle, DollarSign, Database,
  Zap, Shield, Wifi, ChevronRight, Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, accountAPI } from '../api/api';
import TerminalEmulator from '../components/TerminalEmulator';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const cardHoverVariants = {
  rest: { 
    scale: 1,
    boxShadow: "0 0 0 rgba(0, 255, 65, 0)"
  },
  hover: { 
    scale: 1.02,
    boxShadow: "0 0 30px rgba(0, 255, 65, 0.3)",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  }
};

const glowPulse = {
  animate: {
    boxShadow: [
      "0 0 10px rgba(0, 255, 65, 0.3)",
      "0 0 20px rgba(0, 255, 65, 0.5)",
      "0 0 10px rgba(0, 255, 65, 0.3)"
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalMinimized, setTerminalMinimized] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, accountsRes] = await Promise.all([
        dashboardAPI.getStats(),
        accountAPI.getAccounts()
      ]);
      setStats(statsRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ 
              rotate: 360,
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
              scale: { duration: 1, repeat: Infinity, ease: 'easeInOut' }
            }}
            className="relative"
          >
            <Terminal className="w-16 h-16 text-terminal-green" />
            <motion.div
              className="absolute inset-0 border-2 border-terminal-green rounded-full"
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [1, 0, 1]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-terminal-green-dim text-sm"
          >
            INITIALIZING SECURE CONNECTION...
          </motion.p>
          <motion.div 
            className="mt-2 flex justify-center gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-terminal-green"
                animate={{ 
                  opacity: [0.3, 1, 0.3],
                  scale: [0.8, 1, 0.8]
                }}
                transition={{ 
                  duration: 0.8, 
                  repeat: Infinity, 
                  delay: i * 0.2 
                }}
              />
            ))}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-terminal-green p-4 md:p-8 cyber-grid">
      <div className="scanline" />
      
      <motion.div 
        className="max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4"
        >
          <div>
            <motion.h1 
              className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: "spring", stiffness: 100 }}
            >
              CYBERBANK TERMINAL
            </motion.h1>
            <motion.div 
              className="flex items-center gap-2 text-sm terminal-font text-terminal-green-dim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <User className="w-4 h-4" />
              </motion.div>
              <span className="uppercase">{user?.username}</span>
              {user?.role === 'admin' && (
                <motion.span 
                  className="px-2 py-0.5 border border-terminal-green text-xs"
                  animate={{ 
                    boxShadow: [
                      "0 0 5px rgba(0, 255, 65, 0.3)",
                      "0 0 15px rgba(0, 255, 65, 0.5)",
                      "0 0 5px rgba(0, 255, 65, 0.3)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ADMIN
                </motion.span>
              )}
            </motion.div>
          </div>

          <motion.div 
            className="flex gap-2"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowTerminal(!showTerminal)}
              className="terminal-button px-6 py-2 flex items-center gap-2"
              data-testid="terminal-button"
            >
              <Terminal className="w-4 h-4" />
              <span>TERMINAL</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/transfer')}
              className="terminal-button px-6 py-2 flex items-center gap-2"
              data-testid="new-transfer-button"
            >
              <Send className="w-4 h-4" />
              <span>NEW TRANSFER</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="terminal-button px-6 py-2 flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>LOGOUT</span>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          variants={containerVariants}
        >
          <StatCard
            icon={DollarSign}
            label="Total Balance"
            value={`$${stats?.total_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`}
            color="text-terminal-green"
            index={0}
          />
          <StatCard
            icon={Activity}
            label="Total Transfers"
            value={stats?.total_transfers || 0}
            color="text-blue-400"
            index={1}
          />
          <StatCard
            icon={CheckCircle}
            label="Completed"
            value={stats?.completed_transfers || 0}
            color="text-green-400"
            index={2}
          />
          <StatCard
            icon={XCircle}
            label="Failed"
            value={stats?.failed_transfers || 0}
            color="text-red-400"
            index={3}
          />
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Accounts Panel */}
          <motion.div
            variants={itemVariants}
            initial="rest"
            whileHover="hover"
            animate="rest"
            className="terminal-card p-6 relative overflow-hidden"
            data-testid="accounts-panel"
          >
            {/* Animated background gradient */}
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 0% 0%, rgba(0,255,65,0.3) 0%, transparent 50%)",
                  "radial-gradient(circle at 100% 100%, rgba(0,255,65,0.3) 0%, transparent 50%)",
                  "radial-gradient(circle at 0% 0%, rgba(0,255,65,0.3) 0%, transparent 50%)"
                ]
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <CreditCard className="w-5 h-5" />
                </motion.div>
                <h2 className="text-xl font-bold cyber-font">ACCOUNTS</h2>
                <motion.div
                  className="ml-auto text-xs px-2 py-1 border border-terminal-green/50"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {accounts.length} ACTIVE
                </motion.div>
              </div>
              
              <div className="space-y-3">
                <AnimatePresence>
                  {accounts.length > 0 ? (
                    accounts.map((account, index) => (
                      <motion.div
                        key={account.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ 
                          delay: index * 0.1,
                          type: "spring",
                          stiffness: 100
                        }}
                        whileHover={{ 
                          scale: 1.02,
                          x: 5,
                          boxShadow: "0 0 20px rgba(0, 255, 65, 0.2)"
                        }}
                        className="p-4 border border-terminal-green/50 hover:border-terminal-green transition-all cursor-pointer bg-black/30"
                        onClick={() => navigate(`/account/${account.id}`)}
                        data-testid={`account-card-${index}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-xs text-terminal-green-dim">ACCOUNT NUMBER</div>
                            <div className="font-mono text-sm">{account.account_number}</div>
                          </div>
                          <motion.div 
                            className="text-xs px-2 py-1 border border-terminal-green"
                            whileHover={{ scale: 1.1 }}
                          >
                            {account.currency}
                          </motion.div>
                        </div>
                        <div className="flex justify-between items-end mt-3">
                          <div>
                            <div className="text-xs text-terminal-green-dim">BALANCE</div>
                            <motion.div 
                              className="text-xl font-bold"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.3 + index * 0.1 }}
                            >
                              ${account.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </motion.div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-terminal-green-dim">
                              Transfers: {account.previous_transfers}
                            </div>
                            <motion.div
                              animate={{ x: [0, 5, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                            >
                              <ChevronRight className="w-4 h-4 text-terminal-green-dim" />
                            </motion.div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <motion.div 
                      className="text-center py-8 text-terminal-green-dim"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      </motion.div>
                      <p>No accounts found</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions Panel */}
          <motion.div
            variants={itemVariants}
            className="terminal-card p-6 relative overflow-hidden"
          >
            <motion.div
              className="absolute top-0 right-0 w-32 h-32 opacity-5"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Zap className="w-full h-full" />
            </motion.div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Activity className="w-5 h-5" />
                </motion.div>
                <h2 className="text-xl font-bold cyber-font">QUICK ACTIONS</h2>
              </div>
              
              <div className="space-y-3">
                <QuickActionButton
                  icon={Send}
                  label="New Transfer"
                  description="Initiate a new transfer"
                  onClick={() => navigate('/transfer')}
                  testId="quick-action-transfer"
                  index={0}
                />
                <QuickActionButton
                  icon={Clock}
                  label="Transaction History"
                  description="View all transactions"
                  onClick={() => navigate('/transactions')}
                  testId="quick-action-history"
                  index={1}
                />
                {user?.role === 'admin' && (
                  <QuickActionButton
                    icon={Shield}
                    label="Admin Panel"
                    description="Manage users and accounts"
                    onClick={() => navigate('/admin')}
                    testId="quick-action-admin"
                    index={2}
                  />
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* System Status */}
        <motion.div
          variants={itemVariants}
          className="terminal-card p-6 relative overflow-hidden"
        >
          <motion.div
            className="absolute inset-0 data-stream opacity-30"
          />
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <AlertTriangle className="w-5 h-5" />
              </motion.div>
              <h2 className="text-xl font-bold cyber-font">SYSTEM STATUS</h2>
              <motion.span
                className="ml-auto text-xs px-2 py-1 border border-terminal-green bg-terminal-green/10"
                animate={{ 
                  opacity: [0.7, 1, 0.7],
                  boxShadow: [
                    "0 0 5px rgba(0, 255, 65, 0.3)",
                    "0 0 15px rgba(0, 255, 65, 0.5)",
                    "0 0 5px rgba(0, 255, 65, 0.3)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ALL SYSTEMS OPERATIONAL
              </motion.span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatusIndicator label="Database" status="ONLINE" icon={Database} index={0} />
              <StatusIndicator label="Security" status="MAXIMUM" icon={Shield} index={1} />
              <StatusIndicator label="Network" status="STABLE" icon={Wifi} index={2} />
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Terminal Emulator */}
      <AnimatePresence>
        {showTerminal && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
          >
            <TerminalEmulator
              isMinimized={terminalMinimized}
              onToggleMinimize={() => setTerminalMinimized(!terminalMinimized)}
              onClose={() => setShowTerminal(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, index }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ 
      scale: 1.03,
      y: -5,
      boxShadow: "0 10px 30px rgba(0, 255, 65, 0.2)"
    }}
    className="terminal-card p-6 relative overflow-hidden group"
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-br from-terminal-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
    />
    
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-2">
        <motion.div
          animate={{ 
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
          className={color}
        >
          <Icon className="w-6 h-6" />
        </motion.div>
        <motion.span 
          className="text-2xl font-bold cyber-font"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
        >
          {value}
        </motion.span>
      </div>
      <div className="text-sm text-terminal-green-dim uppercase tracking-wider">
        {label}
      </div>
    </div>
    
    {/* Animated corner accent */}
    <motion.div
      className="absolute bottom-0 right-0 w-16 h-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.1 }}
      transition={{ delay: 0.5 + index * 0.1 }}
    >
      <Icon className="w-full h-full" />
    </motion.div>
  </motion.div>
);

const QuickActionButton = ({ icon: Icon, label, description, onClick, testId, index }) => (
  <motion.button
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.1 * index, type: "spring" }}
    whileHover={{ 
      x: 10,
      boxShadow: "0 0 20px rgba(0, 255, 65, 0.2)",
      borderColor: "rgba(0, 255, 65, 1)"
    }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="w-full p-4 border border-terminal-green/50 hover:border-terminal-green transition-all text-left group relative overflow-hidden"
    data-testid={testId}
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-terminal-green/0 via-terminal-green/10 to-terminal-green/0"
      initial={{ x: "-100%" }}
      whileHover={{ x: "100%" }}
      transition={{ duration: 0.6 }}
    />
    
    <div className="flex items-start gap-3 relative z-10">
      <motion.div
        whileHover={{ rotate: 360, scale: 1.2 }}
        transition={{ duration: 0.5 }}
      >
        <Icon className="w-5 h-5 mt-0.5 group-hover:text-terminal-green transition-colors" />
      </motion.div>
      <div className="flex-1">
        <div className="font-bold mb-1 group-hover:glow-text-static transition-all">{label}</div>
        <div className="text-xs text-terminal-green-dim">{description}</div>
      </div>
      <motion.div
        animate={{ x: [0, 5, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ChevronRight className="w-5 h-5" />
      </motion.div>
    </div>
  </motion.button>
);

const StatusIndicator = ({ label, status, icon: Icon, index }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 + index * 0.1 }}
    whileHover={{ scale: 1.02, boxShadow: "0 0 15px rgba(0, 255, 65, 0.2)" }}
    className="flex items-center gap-3 p-3 border border-terminal-green/30 hover:border-terminal-green/60 transition-all"
  >
    <motion.div
      animate={{ 
        scale: [1, 1.2, 1],
        opacity: [0.5, 1, 0.5]
      }}
      transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.3 }}
      className="w-3 h-3 bg-terminal-green rounded-full"
    />
    <div className="flex-1">
      <div className="text-xs text-terminal-green-dim">{label}</div>
      <div className="text-sm font-bold">{status}</div>
    </div>
    <motion.div
      animate={{ rotate: [0, 360] }}
      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      className="opacity-30"
    >
      <Icon className="w-4 h-4" />
    </motion.div>
  </motion.div>
);

export default Dashboard;
